import lodash from 'lodash';
import uuid from 'uuid';

import { getAppConfig } from 'common/util/configUtil';
import FileUploadProcessor, { UploadStatus } from 'common/util/FileUploadProcessor';
import { addApplicationMessage } from 'common/actions/applicationMessagesActions';
import { addUploadFailedDocument } from 'common/actions/fileUploadActions';

class FileUploader {
    constructor({binderId, institutionId, allowedMimeTypes, fileExtensionLookup, store}) {
        this.binderId = binderId;
        this.institutionId = institutionId;
        this.filesRead = [];
        this.allowedMimeTypes = allowedMimeTypes;
        this.fileExtensionLookup = fileExtensionLookup;
        this.store = store;
        this.dispatch = store.dispatch;
        this.addApplicationMessage = (type, message) => this.dispatch(addApplicationMessage(type, message));
        this.addUploadFailedDocument = (fileName) => this.dispatch(addUploadFailedDocument(fileName));
    }

    fileUploadProcessorEvent(file) {
        const updatedFiles = [...this.filesRead];
        const index = lodash.findIndex(updatedFiles, item => {
            return item.fileName === file.fileName;
        });

        updatedFiles[index] = file;
    }

    saveFiles() {
        if (!this.filesRead || !this.binderId) {
            return;
        }

        const notifyFileUpdated = file => {
            this.fileUploadProcessorEvent(file);
        };

        const notifyFileError = file => {
            this.fileUploadProcessorEvent(file);
            this.addUploadFailedDocument(file.fileName);
        };

        const uploadOpts = {
            cors: getAppConfig().corsOriginUrl
        };
        const fileUploadProcessor = new FileUploadProcessor(uploadOpts, this.store);
        try{
            fileUploadProcessor.on('start', notifyFileUpdated);
            fileUploadProcessor.on('success', notifyFileUpdated);
            fileUploadProcessor.on('error', notifyFileError);
            fileUploadProcessor.on('progress', notifyFileUpdated);
            fileUploadProcessor.on('complete', notifyFileUpdated);
            fileUploadProcessor.on('cancel', notifyFileUpdated);

            fileUploadProcessor.processFiles(this.filesRead, { binderId: this.binderId, institutionId: this.institutionId });

            this.fileUploadProcessor = fileUploadProcessor;
        } catch (err) {
            this.addApplicationMessage('error', 'Failed to create document for uploaded file.');
        }
        
    }

    static readFile(file, reader) {
        return new Promise((resolve, reject) => {
            if (!reader) {
                reader = new FileReader();
            }

            reader.onerror = () => {
                reject(reader.error);
            };

            reader.onload = () => {
                resolve({
                    fileName: file.name,
                    file,
                    reader,
                    result: reader.result,
                    status: UploadStatus.loaded,
                    progress: 0,
                    progressUnknown: false,
                    error: null,
                    id: uuid.v4(),
                });
            };

            reader.readAsArrayBuffer(file);
        });
    }

    uploadFiles(files) {
        if (typeof FileReader === 'undefined') {
            this.addApplicationMessage('error', 'This browser does not support file uploads. You must use another browser.');
            return;
        }

        // FIXME sometime in the future
        // This is problematic because we are reading all the files we want to
        // upload into memory and _then_ uploading them ( with 5 uploads max at
        // a time). This can potentially crash someone's browser if they try to
        // upload many/large files at once.
        const fileReads = files.map(file => {
            return FileUploader.readFile(file).then(result => {

                const fileExtRegexp = /\.([^.]+)$/;
                const fileExtMatch = result.file.name.match(fileExtRegexp, 'i');
                if (!fileExtMatch) {
                    this.addUploadFailedDocument(result.file.name);
                    return;
                }

                const [, _fileExt] = fileExtMatch;
                const fileExt = _fileExt.toLowerCase();
                if (fileExt !== 'msg' && this.fileExtensionLookup[result.file.type] !== fileExt) {
                    this.addUploadFailedDocument(result.file.name);
                    return;
                }

                if (fileExt !== 'msg' && !this.allowedMimeTypes.includes(result.file.type)) {
                    this.addUploadFailedDocument(result.file.name);
                    return;
                }

                const allFiles = lodash.concat(this.filesRead, [result]);
                this.filesRead = allFiles;
            }).catch(err => {
                this.addApplicationMessage('error', err);
            });
        });
        return Promise.all(fileReads).then(() => {
            this.saveFiles();
        });
    }

    cancelUpload(fileId) {
        if (this.fileUploadProcessor) {
            this.fileUploadProcessor.abort(fileId);
        }
    }
}

export default FileUploader;
